import { useMemo } from "react";
import { MetricPicker } from "@/components/MetricPicker";
import { SegRange } from "@/components/SegRange";
import { fmtMetricValue, getMetric } from "@/lib/metrics";
import type { App, CampaignStatsRow } from "@/lib/types";

type ScoredRow = {
  id: number;
  name: string;
  sub: string;
  metric: number;
  spend: number;
  score: number;
};

function parseNum(v: string | null | undefined): number {
  if (v == null) return 0;
  const n = parseFloat(v);
  return Number.isNaN(n) ? 0 : n;
}

function buildScores(
  rows: CampaignStatsRow[],
  apps: App[],
  metricId: string
): ScoredRow[] {
  const appMap = new Map(apps.map((a) => [a.id, a]));
  const m = getMetric(metricId);
  return rows
    .map((r) => {
      const appName = r.app_id != null ? appMap.get(r.app_id)?.name : null;
      const sub = appName ? appName : "—";
      const v = m.fromRow(r);
      const spend = parseNum(r.spend_usd);
      // "Scaling" score: high ROAS per low spend (log to soften large budgets).
      const roas = parseNum(r.roas);
      const spendFloor = Math.max(spend, 50);
      const score = (roas || 0.01) * (1000 / Math.log10(spendFloor + 10));
      return {
        id: r.campaign_id,
        name: r.campaign_name ?? `Campaign ${r.campaign_id}`,
        sub,
        metric: v ?? 0,
        spend,
        score,
      };
    })
    .filter((r) => r.metric !== 0 || r.spend > 0);
}

function LeaderRow({
  rank,
  row,
  metricId,
  kind = "pos",
  barPct,
}: {
  rank: number;
  row: ScoredRow;
  metricId: string;
  kind?: "pos" | "neg" | "scale";
  barPct: number;
}) {
  const barColor =
    kind === "pos"
      ? "linear-gradient(90deg, #4fc080, #1f9d55)"
      : kind === "neg"
      ? "linear-gradient(90deg, #ef6868, #b91c1c)"
      : "linear-gradient(90deg, var(--c-accent-400), var(--c-accent-600))";
  return (
    <div className={"lb-row " + kind}>
      <div className="lb-rank">{String(rank).padStart(2, "0")}</div>
      <div className="lb-name">
        {row.name}
        <div className="lb-sub">{row.sub}</div>
        <div
          style={{
            marginTop: 4,
            height: 4,
            background: "var(--c-surface-sunk)",
            borderRadius: 999,
            overflow: "hidden",
          }}
        >
          <div
            style={{
              width: `${Math.min(100, Math.max(3, barPct * 100))}%`,
              height: "100%",
              background: barColor,
            }}
          />
        </div>
      </div>
      <div className="lb-val">{fmtMetricValue(metricId, row.metric)}</div>
    </div>
  );
}

function MetricPickerBtn({
  metricId,
  onChange,
}: {
  metricId: string;
  onChange: (id: string) => void;
}) {
  const m = getMetric(metricId);
  return (
    <MetricPicker
      value={metricId}
      onChange={onChange}
      trigger={({ toggle, open }) => (
        <button
          className="btn"
          onClick={toggle}
          style={{ fontSize: 11.5, height: 26 }}
          data-open={open}
        >
          <span style={{ color: "var(--c-text-3)", marginRight: 4 }}>by</span>
          <span
            className="metric-swatch"
            style={{ background: m.color }}
          />
          {m.short}
        </button>
      )}
    />
  );
}

export function TopPerformingCard({
  rows,
  apps,
  metricId,
  onMetricChange,
  count = 4,
  loading,
}: {
  rows: CampaignStatsRow[];
  apps: App[];
  metricId: string;
  onMetricChange: (id: string) => void;
  count?: number;
  loading?: boolean;
}) {
  const m = getMetric(metricId);
  const sorted = useMemo(() => {
    const scored = buildScores(rows, apps, metricId);
    return scored
      .sort((a, b) =>
        m.goodDir === "up" ? b.metric - a.metric : a.metric - b.metric
      )
      .slice(0, count);
  }, [rows, apps, metricId, m.goodDir, count]);
  const ref = sorted[0]?.metric || 1;

  return (
    <div className="lb-card">
      <div className="card-head">
        <div className="row gap-8">
          <div className="lb-head-ico pos" style={headIcoStyle("pos")}>
            ▲
          </div>
          <h3>Top performing</h3>
        </div>
        <MetricPickerBtn metricId={metricId} onChange={onMetricChange} />
      </div>
      {loading && sorted.length === 0 && (
        <div className="muted" style={{ padding: 16, fontSize: 12.5 }}>
          Loading…
        </div>
      )}
      {!loading && sorted.length === 0 && (
        <div className="muted" style={{ padding: 16, fontSize: 12.5 }}>
          No data in this period.
        </div>
      )}
      {sorted.map((r, i) => (
        <LeaderRow
          key={r.id}
          rank={i + 1}
          row={r}
          metricId={metricId}
          kind="pos"
          barPct={
            m.goodDir === "up"
              ? r.metric / (ref || 1)
              : (ref || 1) / Math.max(Math.abs(r.metric), 0.0001)
          }
        />
      ))}
    </div>
  );
}

export function UnderPerformingCard({
  rows,
  apps,
  metricId,
  onMetricChange,
  count = 4,
  loading,
}: {
  rows: CampaignStatsRow[];
  apps: App[];
  metricId: string;
  onMetricChange: (id: string) => void;
  count?: number;
  loading?: boolean;
}) {
  const m = getMetric(metricId);
  const sorted = useMemo(() => {
    const scored = buildScores(rows, apps, metricId);
    // Underperformers = spent money but metric is bad. Require min spend.
    return scored
      .filter((r) => r.spend > 10)
      .sort((a, b) =>
        m.goodDir === "up" ? a.metric - b.metric : b.metric - a.metric
      )
      .slice(0, count);
  }, [rows, apps, metricId, m.goodDir, count]);
  const ref = sorted[0]?.metric || 1;

  return (
    <div className="lb-card">
      <div className="card-head">
        <div className="row gap-8">
          <div className="lb-head-ico neg" style={headIcoStyle("neg")}>
            ▼
          </div>
          <h3>Underperforming</h3>
        </div>
        <MetricPickerBtn metricId={metricId} onChange={onMetricChange} />
      </div>
      {loading && sorted.length === 0 && (
        <div className="muted" style={{ padding: 16, fontSize: 12.5 }}>
          Loading…
        </div>
      )}
      {!loading && sorted.length === 0 && (
        <div className="muted" style={{ padding: 16, fontSize: 12.5 }}>
          Nothing to flag.
        </div>
      )}
      {sorted.map((r, i) => (
        <LeaderRow
          key={r.id}
          rank={i + 1}
          row={r}
          metricId={metricId}
          kind="neg"
          barPct={
            m.goodDir === "up"
              ? (ref || 1) / Math.max(Math.abs(r.metric), 0.0001)
              : r.metric / (ref || 1)
          }
        />
      ))}
    </div>
  );
}

export function ScalingCard({
  rows,
  apps,
  metricId,
  onMetricChange,
  basis,
  onBasisChange,
  loading,
}: {
  rows: CampaignStatsRow[];
  apps: App[];
  metricId: string;
  onMetricChange: (id: string) => void;
  basis: "campaigns" | "keywords";
  onBasisChange: (b: "campaigns" | "keywords") => void;
  loading?: boolean;
}) {
  const sorted = useMemo(() => {
    const scored = buildScores(rows, apps, metricId);
    // Highest score first. Score favours high ROAS + moderate spend.
    return scored.sort((a, b) => b.score - a.score).slice(0, 4);
  }, [rows, apps, metricId]);
  const ref = sorted[0]?.score || 1;

  return (
    <div className="lb-card">
      <div className="card-head">
        <div className="row gap-8">
          <div className="lb-head-ico scale" style={headIcoStyle("scale")}>
            ↗
          </div>
          <h3>Scaling candidates</h3>
        </div>
        <div className="row gap-8">
          <SegRange<"campaigns" | "keywords">
            value={basis}
            onChange={onBasisChange}
            options={[
              { value: "campaigns", label: "Camp." },
              { value: "keywords", label: "Keys" },
            ]}
          />
          <MetricPickerBtn metricId={metricId} onChange={onMetricChange} />
        </div>
      </div>
      {loading && sorted.length === 0 && (
        <div className="muted" style={{ padding: 16, fontSize: 12.5 }}>
          Loading…
        </div>
      )}
      {!loading && sorted.length === 0 && (
        <div className="muted" style={{ padding: 16, fontSize: 12.5 }}>
          No scaling opportunities yet.
        </div>
      )}
      {sorted.map((r, i) => (
        <LeaderRow
          key={r.id}
          rank={i + 1}
          row={r}
          metricId={metricId}
          kind="scale"
          barPct={r.score / (ref || 1)}
        />
      ))}
    </div>
  );
}

function headIcoStyle(kind: "pos" | "neg" | "scale"): React.CSSProperties {
  const bg =
    kind === "pos"
      ? "rgba(31,157,85,0.12)"
      : kind === "neg"
      ? "rgba(220,38,38,0.12)"
      : "rgba(110,98,255,0.12)";
  const color =
    kind === "pos"
      ? "var(--c-pos-500)"
      : kind === "neg"
      ? "var(--c-neg-500)"
      : "var(--c-accent-600)";
  return {
    width: 26,
    height: 26,
    borderRadius: 8,
    background: bg,
    color,
    display: "grid",
    placeItems: "center",
    fontSize: 12,
    flex: "none",
  };
}
